import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday,
  Scale,
  Female,
  Male,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface BirthFeedbackProps {
  birthData: any;
}

const BirthFeedback: React.FC<BirthFeedbackProps> = ({ birthData }) => {
  const { t } = useTranslation(['animal', 'common']);
  
  // Extrair dados do nascimento - os dados podem estar em birthData.data.data ou birthData.data
  const data = birthData?.data?.data || birthData?.data || {};
  const resumo = birthData?.resumo || birthData?.data?.resumo || '';
  
  // Extrair informações dos dados estruturados
  const bezerroTag = data.name || data.cattle_tag || data.bezerro_tag || t('common:not_informed');
  const vacaTag = data.dam_cattle_tag || data.mother_tag || data.vaca_tag || data.mother_cattle_tag || t('common:not_informed');
  const peso = data.weight_kg || data.birth_weight_kg || data.peso || t('common:not_informed');
  const dataNascimento = data.birth_date || data.data_nascimento || data.handling_date || t('common:not_informed');
  const sexo = data.gender || data.sexo || t('common:not_informed');

  // Logs de debug removidos

  // Função para formatar data
  const formatDate = (dateString: string) => {
    if (dateString === t('common:not_informed')) return dateString;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };
  
  // Função para formatar peso
  const formatWeight = (weight: any) => {
    if (weight === t('common:not_informed') || !weight) return t('common:not_informed');
    if (typeof weight === 'number') return `${weight} kg`;
    if (typeof weight === 'string' && weight.includes('kg')) return weight;
    return `${weight} kg`;
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 60px)', overflow: 'auto' }}>
      {/* Header Minimalista */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <img
              src="/vaca.svg"
              alt={t('animal:birth_feedback.title')}
              style={{
                width: 32,
                height: 32,
                color: DESIGN_TOKENS.colors.organization.farm
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: DESIGN_TOKENS.colors.organization.farm,
                  mb: 0.5
                }}
              >
                {t('animal:birth_feedback.title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              >
                {t('animal:birth_feedback.description')}
              </Typography>
            </Box>
            <Chip
              label={t('common:success')}
              color="success"
              sx={{ 
                fontWeight: 600,
                backgroundColor: DESIGN_TOKENS.colors.brand.primary,
                color: 'white'
              }}
            />
          </Box>
          
          {/* Resumo em chips */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<img src="/vaca.svg" alt={t('animal:birth_feedback.calf')} style={{ width: '16px', height: '16px' }} />}
              label={`${t('animal:birth_feedback.calf')} ${bezerroTag}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            {sexo !== t('common:not_informed') && (
              <Chip
                icon={sexo?.toLowerCase() === t('common:gender.male').toLowerCase() ? 
                  <Male sx={{ fontSize: 16 }} /> : 
                  <Female sx={{ fontSize: 16 }} />
                }
                label={sexo}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Informações Detalhadas */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: DESIGN_TOKENS.colors.text.primary
            }}
          >
            {t('animal:birth_feedback.birth_information')}
          </Typography>
          
          {/* Grid de informações */}
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {/* Bezerro */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <img src="/vaca.svg" alt={t('animal:birth_feedback.calf')} style={{ width: '20px', height: '20px', opacity: 0.6 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('animal:birth_feedback.calf')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {bezerroTag}
              </Typography>
            </Box>
            
            {/* Vaca */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <img src="/vinculo.svg" alt={t('animal:birth_feedback.mother_cow')} style={{ width: '20px', height: '20px', opacity: 0.6 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('animal:birth_feedback.mother_cow')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {vacaTag}
              </Typography>
            </Box>
            
            {/* Data de Nascimento */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarToday sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('common:fields.birth_date')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDate(dataNascimento)}
              </Typography>
            </Box>
            
            {/* Peso */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Scale sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('common:fields.weight')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatWeight(peso)}
              </Typography>
            </Box>
          </Box>
          
          {/* Resumo */}
          {resumo && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, mb: 1 }}
                >
                  {t('common:summary')}
                </Typography>
                <Typography
                   variant="body1"
                   sx={{
                     backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                     p: 2,
                     borderRadius: 1,
                     lineHeight: 1.6
                   }}
                 >
                  {resumo}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BirthFeedback;